import React, { useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import openPopupOperation from 'editor/src/store/app/module/popup/operation/openPopupOperation';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getFocusedContent from 'editor/src/store/editor/selector/getFocusedContent';
import getSelectedElementUuids from 'editor/src/store/editor/selector/getSelectedElementUuids';
import undoStoreOperation from 'editor/src/store/editorModules/undoRedo/operation/undoStoreOperation';
import getGalleryImages from 'editor/src/store/gallery/selector/getGalleryImages';
import {
  DEFAULT_MAX_TOTAL_COPIES,
  MAX_GAP_MM,
  MAX_MARGIN_MM,
  MIN_GAP_MM,
  MIN_MARGIN_MM,
} from 'editor/src/store/gangSheet/constants';
import autoNestOperation from 'editor/src/store/gangSheet/operation/autoNestOperation';
import fillSheetWithArtworkOperation from 'editor/src/store/gangSheet/operation/fillSheetWithArtworkOperation';
import setArtworkQtyOperation from 'editor/src/store/gangSheet/operation/setArtworkQtyOperation';
import getResolvedAllowRotate from 'editor/src/store/gangSheet/selector/getResolvedAllowRotate';
import getResolvedGapMm from 'editor/src/store/gangSheet/selector/getResolvedGapMm';
import getResolvedMarginMm from 'editor/src/store/gangSheet/selector/getResolvedMarginMm';
import { setAllowRotateAction, setGapMmAction, setMarginMmAction } from 'editor/src/store/gangSheet/slice';
import getArtworkGroups from 'editor/src/store/gangSheet/util/getArtworkGroups';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { NestResult } from 'editor/src/util/gangSheet/nestOnSheet';

import Button from 'editor/src/component/Button';
import ContentBlock from 'editor/src/component/DesktopSidebar/TabContents/Elements/ContentBlock';
import TabContentHeader from 'editor/src/component/DesktopSidebar/TabContents/TabContentHeader';
import { PopupName } from 'editor/src/component/Popup/popups';
import SwitchControl from 'editor/src/component/SwitchControl';

import ArtworkRow from './ArtworkRow';
import QuantityStepper from './QuantityStepper';

import styles from './index.module.scss';

function GangSheetTabContent() {
  const dispatch = useDispatch();

  const { gangSheetSettings, images, spread, focusedContent, selectedElementUuids } = useSelector(
    (state) => ({
      gangSheetSettings: state.hostSettings.gangSheet,
      images: getGalleryImages(state),
      spread: state.design.designData?.spreads[getCurrentSpreadIndex(state)],
      focusedContent: getFocusedContent(state),
      selectedElementUuids: getSelectedElementUuids(state),
    }),
    shallowEqual,
  );
  const gapMm = useSelector(getResolvedGapMm);
  const allowRotate = useSelector(getResolvedAllowRotate);
  const marginMm = useSelector(getResolvedMarginMm);

  // the canvas is the source of truth: one row per artwork-at-one-size group; rows
  // appear, split and disappear as the buyer adds, resizes and removes elements
  const groups = useMemo(() => getArtworkGroups(spread), [spread]);
  const area = useMemo(() => getContentAreaFromSpread(spread, focusedContent), [spread, focusedContent]);

  const imagesById = useMemo(() => new Map(images.map((image) => [image.id, image])), [images]);

  // groups containing a canvas-selected element get highlighted in the list
  const selectedGroupKeys = useMemo(() => {
    const selectedUuids = new Set(selectedElementUuids);
    return new Set(
      groups
        .filter((group) => group.elements.some((element) => selectedUuids.has(element.uuid)))
        .map((group) => group.key),
    );
  }, [groups, selectedElementUuids]);

  const maxTotalCopies = gangSheetSettings?.maxTotalCopies ?? DEFAULT_MAX_TOTAL_COPIES;
  const totalCopies = useMemo(() => groups.reduce((sum, group) => sum + group.elements.length, 0), [groups]);
  const remainingCap = Math.max(0, maxTotalCopies - totalCopies);

  const usedRatio = useMemo(() => {
    if (!area || area.width <= 0 || area.height <= 0) {
      return 0;
    }
    let placedArea = 0;
    groups.forEach((group) => {
      placedArea += group.widthMm * group.heightMm * group.elements.length;
    });
    return Math.min(1, placedArea / (area.width * area.height));
  }, [area, groups]);

  const showOverflowDialog = useCallback(
    (result: NestResult) => {
      const totalUnplaced = result.unplaced.reduce((sum, entry) => sum + entry.count, 0);
      if (totalUnplaced === 0) {
        return;
      }

      dispatch(
        openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
          // TODO: Lokalino
          popupTitle:
            totalUnplaced === 1
              ? "1 copy doesn't fit on this sheet"
              : `${totalUnplaced} copies don't fit on this sheet`,
          textLines: [
            // TODO: Lokalino
            'The copies that did not fit were taken off the sheet. Keep what fits, or undo the nest to restore the previous layout.',
          ],
          options: [
            {
              // TODO: Lokalino
              title: 'Undo nest',
              onConfirm: () => {
                dispatch(undoStoreOperation());
              },
            },
            {
              // TODO: Lokalino
              title: 'Keep what fits',
              onConfirm: () => undefined,
            },
          ],
          hideTitleIcon: true,
        }),
      );
    },
    [dispatch],
  );

  const onAutoNest = useCallback(() => {
    const result = dispatch(autoNestOperation());
    if (result) {
      showOverflowDialog(result);
    }
  }, [dispatch, showOverflowDialog]);

  const onFillSheet = useCallback(
    (groupKey: string) => {
      const result = dispatch(fillSheetWithArtworkOperation(groupKey));
      if (result) {
        showOverflowDialog(result);
      }
    },
    [dispatch, showOverflowDialog],
  );

  const onQtyChange = useCallback(
    (groupKey: string, qty: number) => {
      dispatch(setArtworkQtyOperation(groupKey, qty));
    },
    [dispatch],
  );

  const onRemove = useCallback(
    (groupKey: string) => {
      dispatch(setArtworkQtyOperation(groupKey, 0));
    },
    [dispatch],
  );

  if (!gangSheetSettings) {
    return null;
  }

  return (
    <>
      {/* TODO: Lokalino */}
      <TabContentHeader title="Gang sheet" />
      <ContentBlock scroll fillFlex className={styles.gangSheetTabContent}>
        {groups.length === 0 ? (
          // TODO: Lokalino
          <div className={styles.hint}>
            Add artwork to the sheet from the Files tab — every design on the sheet shows up here.
          </div>
        ) : (
          <div className={styles.artworkList}>
            {groups.map((group) => (
              <ArtworkRow
                key={group.key}
                group={group}
                image={imagesById.get(group.imageId)}
                selected={selectedGroupKeys.has(group.key)}
                incrementDisabled={remainingCap <= 0}
                onQtyChange={onQtyChange}
                onFillSheet={onFillSheet}
                onRemove={onRemove}
              />
            ))}
          </div>
        )}
        <div className={styles.options}>
          <div className={styles.optionRow}>
            {/* TODO: Lokalino */}
            <div className={styles.optionLabel}>Gap between designs</div>
            <QuantityStepper
              value={gapMm ?? 0}
              min={MIN_GAP_MM}
              max={MAX_GAP_MM}
              suffix="mm"
              onChange={(value) => dispatch(setGapMmAction(value))}
            />
          </div>
          <div className={styles.optionRow}>
            {/* TODO: Lokalino */}
            <div className={styles.optionLabel}>Margin from sheet edge</div>
            <QuantityStepper
              value={marginMm}
              min={MIN_MARGIN_MM}
              max={MAX_MARGIN_MM}
              suffix="mm"
              onChange={(value) => dispatch(setMarginMmAction(value))}
            />
          </div>
          <SwitchControl
            // TODO: Lokalino
            title="Allow 90° rotation"
            on={allowRotate ?? false}
            onSwitch={(on) => dispatch(setAllowRotateAction(on))}
            className={styles.rotationSwitch}
          />
          {/* TODO: Lokalino */}
          <div className={styles.optionNote}>Applied on the next Auto nest.</div>
        </div>
      </ContentBlock>
      <div className={styles.footer}>
        <div className={styles.summary}>
          {/* TODO: Lokalino */}
          <div className={styles.summaryItem}>{`Total copies: ${totalCopies} of max ${maxTotalCopies}`}</div>
          {/* TODO: Lokalino */}
          <div className={styles.summaryItem}>{`Sheet used: ${Math.round(usedRatio * 100)}%`}</div>
        </div>
        <Button
          primary
          fullWidth
          disabled={groups.length === 0}
          className="cy-gang-sheet-auto-nest-button"
          onClick={onAutoNest}
        >
          {/* TODO: Lokalino */}
          Auto nest sheet
        </Button>
      </div>
    </>
  );
}

export default React.memo(GangSheetTabContent);
